/**
 * The Attached row's chips (SA-1304; wireframe decision 14): a snapshot grouped into up to four
 * chips, each previewing exactly what it carries, and the payload with removed chips left out.
 */
import type { FeedbackEvent } from './allowlist'
import type { FeedbackSnapshot } from './environment'
import {
  CHIP_LABELS,
  apiErrorLine,
  browserLine,
  buildLine,
  changedLine,
  clickedLine,
  errorsLabel,
  featuresLine,
  openedLine,
  tourLine,
  requestsLine,
  screenLine,
  VIEW_AS_LINE,
} from './feedbackContextText'

export type ChipId = 'page' | 'errors' | 'actions' | 'environment'

export interface ContextChip {
  id: ChipId
  label: string
  preview: string[]
}

/** What the payload carries once some chips are removed; `environment` absent when removed. */
export interface FeedbackPayload {
  environment?: Record<string, unknown>
  events: FeedbackEvent[]
}

function isErrorEvent(e: FeedbackEvent): boolean {
  return e.kind === 'error' || (e.kind === 'api' && typeof e.status === 'number' && (e.status >= 400 || e.status === 0))
}

function latestNav(events: FeedbackEvent[]): FeedbackEvent | undefined {
  for (let i = events.length - 1; i >= 0; i--) if (events[i].kind === 'nav') return events[i]
  return undefined
}

/** Which chip an event belongs to: the latest nav is the page, errors are errors, the rest steps. */
function chipOf(event: FeedbackEvent, page: FeedbackEvent | undefined): ChipId {
  if (event === page) return 'page'
  if (isErrorEvent(event)) return 'errors'
  return 'actions'
}

function pathWithIds(nav: FeedbackEvent): string {
  const params = Array.isArray(nav.params) ? [...(nav.params as string[])] : []
  return String(nav.path ?? '').replace(/:id/g, () => params.shift() ?? ':id')
}

function where(nav: FeedbackEvent): string {
  return typeof nav.title === 'string' && nav.title ? nav.title : pathWithIds(nav)
}

function stepLine(e: FeedbackEvent): string | null {
  if (e.kind === 'click') return clickedLine(String(e.name))
  if (e.kind === 'change') return changedLine(String(e.field))
  if (e.kind === 'nav') return openedLine(where(e))
  if (e.kind === 'tour') return tourLine(String(e.action), String(e.tour), e.step as number | undefined)
  return null
}

function errorLine(e: FeedbackEvent): string {
  if (e.kind === 'api') return apiErrorLine(String(e.method), String(e.path), Number(e.status), e.code as string | undefined)
  const message = typeof e.message === 'string' ? e.message : ''
  return message || String(e.code ?? e.source)
}

function environmentLines(env: Record<string, unknown>): string[] {
  const lines = [
    browserLine(env.browser as string, env.browser_version as number, env.platform as string),
  ]
  if (typeof env.build === 'string') lines.push(buildLine(env.build))
  if (typeof env.viewport_w === 'number' && typeof env.viewport_h === 'number') {
    lines.push(screenLine(env.viewport_w, env.viewport_h, env.theme as string | undefined))
  }
  if (Array.isArray(env.features) && env.features.length > 0) lines.push(featuresLine(env.features.length))
  if (env.view_as === true) lines.push(VIEW_AS_LINE)
  return lines
}

export function contextChips(snapshot: FeedbackSnapshot): ContextChip[] {
  const page = latestNav(snapshot.events)
  const chips: ContextChip[] = []
  if (page) {
    const preview = [where(page)]
    if (preview[0] !== pathWithIds(page)) preview.push(pathWithIds(page))
    chips.push({ id: 'page', label: CHIP_LABELS.page, preview })
  }
  const errors = snapshot.events.filter((e) => chipOf(e, page) === 'errors')
  if (errors.length > 0) chips.push({ id: 'errors', label: errorsLabel(errors.length), preview: errors.map(errorLine) })
  const steps = snapshot.events.filter((e) => chipOf(e, page) === 'actions')
  if (steps.length > 0) {
    const preview = steps.map(stepLine).filter((l): l is string => l !== null)
    const requests = steps.filter((e) => e.kind === 'api').length
    if (requests > 0) preview.push(requestsLine(requests))
    chips.push({ id: 'actions', label: CHIP_LABELS.actions, preview })
  }
  if (Object.keys(snapshot.environment).length > 0) {
    chips.push({ id: 'environment', label: CHIP_LABELS.environment, preview: environmentLines(snapshot.environment) })
  }
  return chips
}

/** The payload with the removed chips' parts left out entirely. */
export function withoutChips(snapshot: FeedbackSnapshot, removed: Iterable<ChipId>): FeedbackPayload {
  const gone = new Set(removed)
  const page = latestNav(snapshot.events)
  const payload: FeedbackPayload = { events: snapshot.events.filter((e) => !gone.has(chipOf(e, page))) }
  if (!gone.has('environment')) payload.environment = snapshot.environment
  return payload
}
